import React, { useEffect, useState } from 'react';
import { useTable, usePagination, useSortBy } from 'react-table';
import { regexPatterns } from '../../constants';
import beginningArrows from '../../assests/images/beginningArrows.gif';
import endArrows from '../../assests/images/endArrows.gif';
import nextArrow from '../../assests/images/nextArrow.gif';
import previousArrow from '../../assests/images/previousArrow.gif';
import sortIcon from '../../assests/images/sortIcon.png';
import sortingUpArrow from '../../assests/images/SortingUpArrow.svg';
import sortingDownArrow from '../../assests/images/SortingDownArrow.png';
import './TablePagination.css';
import { Link } from 'react-router-dom';

const getStatusClassName = (status) => {
  return status === 'Active' ? 'status-active' : 'status-inactive';
};

function TablePaginationComponent({
  data,
  customColumns,
  headerText,
  headerClassName,
  tableClassName,
  tableHeadingClassName,
  tableDataClassName,
  footerClassName,
  footerText,
  columnsToFormat,
  formatSymbol,
  columnsToSum,
  pageChangeHandler,
  currentPage,
  totalRows,
  rowsPerPage,
  setRowsPerPage,
  headersWithTooltip = [],
  columnWidths = [],
  columnMaxWidth = [],
  columnWidth = [],
  hrefColumns = [],
  selectedRows,
  handleCheckboxChange,
  onSelectAll,
  pageOptions = [],
}) {
  const [gotoPageInput, setGotoPageInput] = useState('');
  const [gotoPageInputText, setGotoPageInputText] = useState('Goto');
  const [pageSizeOptions, setPageSizeOptions] = useState([10, 20, 50, 100]);

  useEffect(() => {
    if (pageOptions.length > 0) setPageSizeOptions(pageOptions);
  }, [pageOptions]);

  // Pass the rows array to the parent component's handler for 'Select All' functionality.
  const handleSelectAllChange = (e) => {
    const isSelected = e.target.checked;
    const allRowIds = isSelected ? rows.map((row) => row.id) : [];
    onSelectAll(allRowIds); // Pass this back to parent
  };

  const columns = React.useMemo(() => {
    if (!customColumns || customColumns.length === 0) {
      if (!data || data.length === 0) {
        return [];
      }
      const firstDataRow = data[0];
      return Object.keys(firstDataRow).map((key) => {
        const formattedHeader = key
          .split(' ')
          .map((word) => {
            return word.replace(regexPatterns.UpperCase, ' $1');
          })
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return {
          Header: ({ column }) => (
            <span>
              {column.id === 'Status' ? (
                <>
                  {column.id}{' '}
                  {!column.isSorted ? (
                    <img src={sortIcon} alt='Sort Icon' className='sort-icon' />
                  ) : column.isSortedDesc ? (
                    <img
                      src={sortingDownArrow}
                      alt='Sorted Descending'
                      className='sort-icon'
                    />
                  ) : (
                    <img
                      src={sortingUpArrow}
                      alt='Sorted Ascending'
                      className='sort-icon'
                    />
                  )}
                </>
              ) : (
                formattedHeader
              )}
            </span>
          ),
          accessor: key.replace(/\./g, ''),
          Cell: (row) => {
            if (columnsToFormat && columnsToFormat.includes(key.toLowerCase())) {
              const formattedValue = Number(row.value).toFixed(2);
              return `${formatSymbol}${formattedValue}`;
            }
            return row.value;
          },
          disableSortBy: key !== 'Status',
        };
      });
    } else {
      return customColumns.map((column) => ({
        ...column,
        disableSortBy: column.accessor !== 'Status',
        Header:
          column.accessor === 'Status' ? (
            <span>
              {column.Header}
              <img src={sortIcon} alt='Sort Icon' className='sort-icon' />
            </span>
          ) : (
            column.Header
          ), // Enable sorting only for the "Status" column
      }));
    }
  }, [data, customColumns, columnsToFormat, formatSymbol]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      // Ensures unique row IDs across pages - CHG0303496 by Rakshith N
      getRowId: (row, index) => String((currentPage - 1) * rowsPerPage + index),
      defaultCanSort: true,
      initialState: {
        pageIndex: currentPage - 1,
      },
    },
    useSortBy,
    usePagination
  );

  //console.log("Row IDs on page", currentPage, rows.map((r) => r.id));

  const balanceSum = React.useMemo(() => {
    if (!Array.isArray(columnsToSum) || columnsToSum.length === 0) {
      return 0;
    }
    return data.reduce((sum, row) => {
      const columnSum = columnsToSum.reduce((colSum, column) => colSum + row[column], 0);
      return sum + columnSum;
    }, 0);
  }, [data, columnsToSum]);

  const onNextPage = () => {
    pageChangeHandler(currentPage + 1);
  };
  const onPrevPage = () => {
    pageChangeHandler(currentPage - 1);
  };

  const onGotoPage = (pageNo) => {
    pageChangeHandler(pageNo + 1);
  };

  const handleGotoPageChange = (e) => {
    const { value } = e.target;
    const regex = regexPatterns.AllowsOnlyNumber;

    if (regex.test(value)) {
      const numericValue = parseInt(value, 10);

      // Check if the input value is less than or equal to the maximum page number
      if (numericValue <= noOfPages) {
        setGotoPageInput(value);
      }
      // If numeric value exceeds pageNumber, do nothing
    } else {
      setGotoPageInput('');
    }
  };

  const handleGotoPageKeyPress = (e) => {
    if (e.key === 'Enter') {
      const pageNo = parseInt(gotoPageInput, 10);
      if (!isNaN(pageNo) && pageNo > 0 && pageNo <= noOfPages) {
        onGotoPage(pageNo - 1);
      }
      setGotoPageInput('');
    }
  };

  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    pageChangeHandler(1); // Reset to the first page
  };

  const pageNumbers = [];
  const maxPageButtons = 6;
  const noOfPages = Math.ceil(totalRows / rowsPerPage);
  let startPage = 0;
  let endPage = 0;
  if (noOfPages <= maxPageButtons) {
    startPage = 0;
    endPage = noOfPages - 1;
  } else {
    if (pageIndex <= Math.floor(maxPageButtons / 2)) {
      startPage = 0;
      endPage = maxPageButtons - 1;
    } else if (pageIndex >= noOfPages - Math.floor(maxPageButtons / 2)) {
      startPage = noOfPages - maxPageButtons;
      endPage = noOfPages - 1;
    } else {
      startPage = pageIndex - Math.floor(maxPageButtons / 2);
      endPage = pageIndex + Math.floor(maxPageButtons / 2);
    }
  }
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoNext, setCanGoNext] = useState(true);

  useEffect(() => {
    if (noOfPages === currentPage) {
      setCanGoNext(false);
    } else {
      setCanGoNext(true);
    }
    if (currentPage === 1) {
      setCanGoBack(false);
    } else {
      setCanGoBack(true);
    }
    setGotoPageInputText('Goto 1 - ' + noOfPages);
  }, [noOfPages, currentPage]);

  const getHeaderText = (header) => {
    if (typeof header === 'string') {
      return header;
    } else if (React.isValidElement(header)) {
      if (header.props.children && Array.isArray(header.props.children)) {
        // Find the text node in the children
        const textChild = header.props.children.find(
          (child) => typeof child === 'string'
        );
        return textChild || '';
      }
    }
    return '';
  };

  return (
    <div>
      {
        <div className='paginatorLinks paginatorLinks-top'>
          {pageOptions.length > 1 && (
            <>
              Rows per page:{' '}
              <span className='show-entities'>
                <select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  className='rows-per-page-select'
                >
                  {pageSizeOptions.map((item) => (
                    <option value={item}>{item}</option>
                  ))}
                </select>
              </span>
            </>
          )}
          <span className='show-entities'>
            Showing <b>{pageIndex * rowsPerPage + 1}</b> to{' '}
            <b>{Math.min((pageIndex + 1) * rowsPerPage, totalRows)}</b> of{' '}
            <b>{totalRows}</b> records
          </span>
          {pageNumbers.length > 1 && (
            <div style={{ float: 'right' }}>
              <input
                type='text'
                value={gotoPageInput}
                onChange={handleGotoPageChange}
                onKeyPress={handleGotoPageKeyPress}
                placeholder={gotoPageInputText}
                className='goto-input'
              />
              <a
                href={() => false}
                onClick={() => onGotoPage(0)}
                onKeyDown={() => onGotoPage(0)}
                disabled={!canGoBack}
                className={canGoBack ? '' : 'disabled-link'}
              >
                <img src={beginningArrows} alt='beginningArrows' />
              </a>
              <a
                href={() => false}
                onClick={() => onPrevPage()}
                onKeyDown={() => onPrevPage()}
                disabled={!canGoBack}
                className={canGoBack ? '' : 'disabled-link'}
              >
                <img src={previousArrow} alt='previousArrow' />
              </a>
              {pageNumbers.map((page) => (
                <a
                  href={() => false}
                  key={page}
                  onClick={() => onGotoPage(page)}
                  onKeyDown={() => onGotoPage(page)}
                  className={pageIndex === page ? 'active' : ''}
                >
                  {page + 1}
                </a>
              ))}
              <a
                href={() => false}
                onClick={() => onNextPage()}
                onKeyDown={() => onNextPage()}
                disabled={!canGoNext}
                className={canGoNext ? '' : 'disabled-link'}
              >
                <img src={nextArrow} alt='nextArrow' />
              </a>
              <a
                href={() => false}
                onClick={() => onGotoPage(noOfPages - 1)}
                onKeyDown={() => onGotoPage(noOfPages - 1)}
                disabled={!canGoNext}
                className={canGoNext ? '' : 'disabled-link'}
              >
                <img src={endArrows} alt='endArrows' />
              </a>
            </div>
          )}
        </div>
      }
      <div className='table-container'>
        <div className='table-section'>
          <table {...getTableProps()} className={tableClassName}>
            <thead>
              {headerText && (
                <tr>
                  <th colSpan={columns.length} className={headerClassName}>
                    {headerText}
                  </th>
                </tr>
              )}
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    return (
                      <th
                        className={tableHeadingClassName}
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        key={column.id}
                        style={{
                          minWidth: columnWidths[column.Header] || 'auto',
                          maxWidth: columnMaxWidth[column.Header] || 'auto',
                          width: columnWidths[column.id] || 'auto',
                        }}
                      >
                        {column.id === 'select' ? (
                          <div className='checkboxCell'>
                            <input
                              type='checkbox'
                              onChange={handleSelectAllChange}
                              className='selectAllCheckbox'
                              title='checkAll'
                              checked={selectedRows.length === rows.length}
                            />
                          </div>
                        ) : (
                          column.render('Header')
                        )}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <span></span>
                          ) : (
                            <span></span>
                          )
                        ) : (
                          ''
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      const headerText = getHeaderText(cell.column.id);
                      const isStatusColumn = headerText.includes('Status');
                      const className = isStatusColumn
                        ? getStatusClassName(cell.value)
                        : tableDataClassName;
                      let cellValue = cell.value;
                      const hrefColumn = hrefColumns.find(
                        (hrefColumn) => hrefColumn.hrefColumnName === cell.column.id
                      );

                      if (
                        columnsToFormat &&
                        columnsToFormat.includes(cell.column.id.toLowerCase())
                      ) {
                        cellValue = formatSymbol + String(cellValue);
                      }

                      const checkOverflow = (element) => {
                        return (
                          element.scrollHeight > element.clientHeight ||
                          element.scrollWidth > element.clientWidth
                        );
                      };

                      return (
                        <td
                          className={className}
                          {...cell.getCellProps()}
                          style={{
                            minWidth: columnWidths[cell.column.id] || 'auto',
                            maxWidth: columnMaxWidth[cell.column.id] || 'auto',
                            width: columnWidth[cell.column.id] || 'auto',
                            position: 'relative',
                          }}
                        >
                          <div
                            className='cell-content'
                            ref={(el) => {
                              if (el && checkOverflow(el)) {
                                if (!el.querySelector('.tooltip')) {
                                  const tooltipDiv = document.createElement('div');
                                  tooltipDiv.className = 'tooltip';
                                  tooltipDiv.textContent = cellValue;
                                  el.appendChild(tooltipDiv);
                                }
                              }
                            }}
                          >
                            {cell.column.id === 'select' ? (
                              <div className='checkboxCell'>
                                <input
                                  type='checkbox'
                                  checked={selectedRows.includes(row.id) || false}
                                  className='selectAllCheckbox'
                                  data-testid={`rowSelection_${row?.id}`}
                                  onChange={() => handleCheckboxChange(row)}
                                />
                              </div>
                            ) : hrefColumn ? (
                              <Link
                                className='accountNumber-a'
                                onClick={() =>
                                  hrefColumn.updateAccountAndNavigate(cellValue)
                                }
                              >
                                {cellValue}
                              </Link>
                            ) : (
                              cellValue
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
            {footerText && (
              <tfoot className={footerClassName}>
                <tr>
                  <td colSpan={columns.length - 1}>{footerText}</td>
                  <td>
                    {isNaN(balanceSum) || balanceSum === null
                      ? '0.00'
                      : formatSymbol
                        ? `${formatSymbol}${balanceSum.toFixed(2)}`
                        : balanceSum.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {
        <div className='paginatorLinks paginatorLinks-bottom'>
          {pageOptions.length > 1 && (
            <>
              Rows per page:{' '}
              <span className='show-entities'>
                <select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  className='rows-per-page-select'
                >
                  {pageSizeOptions.map((item) => (
                    <option value={item}>{item}</option>
                  ))}
                </select>
              </span>
            </>
          )}
          <span className='show-entities'>
            Showing <b>{pageIndex * rowsPerPage + 1}</b> to{' '}
            <b>{Math.min((pageIndex + 1) * rowsPerPage, totalRows)}</b> of{' '}
            <b>{totalRows}</b> records
          </span>
          {pageNumbers.length > 1 && (
            <div style={{ float: 'right' }}>
              <input
                type='text'
                value={gotoPageInput}
                onChange={handleGotoPageChange}
                onKeyPress={handleGotoPageKeyPress}
                placeholder={gotoPageInputText}
                className='goto-input'
              />
              <a
                href={() => false}
                onClick={() => onGotoPage(0)}
                onKeyDown={() => onGotoPage(0)}
                disabled={!canGoBack}
                className={canGoBack ? '' : 'disabled-link'}
              >
                <img src={beginningArrows} alt='beginningArrows' />
              </a>
              <a
                href={() => false}
                onClick={() => onPrevPage()}
                onKeyDown={() => onPrevPage()}
                disabled={!canGoBack}
                className={canGoBack ? '' : 'disabled-link'}
              >
                <img src={previousArrow} alt='previousArrow' />
              </a>
              {pageNumbers.map((page) => (
                <a
                  href={() => false}
                  key={page}
                  onClick={() => onGotoPage(page)}
                  onKeyDown={() => onGotoPage(page)}
                  className={pageIndex === page ? 'active' : ''}
                >
                  {page + 1}
                </a>
              ))}
              <a
                href={() => false}
                onClick={() => onNextPage()}
                onKeyDown={() => onNextPage()}
                disabled={!canGoNext}
                className={canGoNext ? '' : 'disabled-link'}
              >
                <img src={nextArrow} alt='nextArrow' />
              </a>
              <a
                href={() => false}
                onClick={() => onGotoPage(noOfPages - 1)}
                onKeyDown={() => onGotoPage(noOfPages - 1)}
                disabled={!canGoNext}
                className={canGoNext ? '' : 'disabled-link'}
              >
                <img src={endArrows} alt='endArrows' />
              </a>
            </div>
          )}
        </div>
      }
    </div>
  );
}

export default TablePaginationComponent;
